import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Building2, CheckCircle, XCircle, Pencil, FileText, Link2, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDealStore } from '@/store/dealStore';
import { useActivityStore } from '@/store/activityStore';
import { useNoteStore } from '@/store/noteStore';
import { useReminderStore } from '@/store/reminderStore';
import { formatDate, formatCurrency } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { ActivityTimeline } from '@/components/activities/ActivityTimeline';
import { ActivityFormDialog } from '@/components/activities/ActivityFormDialog';
import { DealFormDialog } from '@/components/deals/DealFormDialog';
import { NoteList } from '@/components/notes/NoteList';
import { ReminderList } from '@/components/reminders/ReminderList';
import { AttachmentSection } from '@/components/attachments/AttachmentSection';
import type { Note, Reminder } from '@/types/crm';

const customerTypeLabels: Record<string, string> = {
  oem: 'OEM',
  dealer: 'Dealer',
  direct: 'Direct Customer',
};

export function DealDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedDeal, stages, fetchDealById, fetchStages, updateDealStage, markDealWon, markDealLost } = useDealStore();
  const { fetchActivitiesByEntity } = useActivityStore();
  const { notes, fetchNotesByDeal } = useNoteStore();
  const { reminders, fetchRemindersByDeal } = useReminderStore();
  const { toast } = useToast();
  const [activities, setActivities] = useState<any[]>([]);
  const [localNotes, setLocalNotes] = useState<Note[]>([]);
  const [localReminders, setLocalReminders] = useState<Reminder[]>([]);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (id) {
        setIsLoading(true);
        await fetchStages();
        await fetchDealById(id);
        const acts = await fetchActivitiesByEntity('deal', id);
        setActivities(acts);
        const nts = await fetchNotesByDeal(id);
        setLocalNotes(nts);
        const rems = await fetchRemindersByDeal(id);
        setLocalReminders(rems);
        setIsLoading(false);
      }
    };
    loadData();
  }, [id, fetchDealById, fetchStages, fetchActivitiesByEntity, fetchNotesByDeal, fetchRemindersByDeal]);

  useEffect(() => {
    setLocalNotes(notes);
  }, [notes]);

  useEffect(() => {
    setLocalReminders(reminders);
  }, [reminders]);

  const handleStageChange = async (stage: string) => {
    if (id) {
      try {
        await updateDealStage(id, stage);
        await fetchDealById(id);
        toast({ title: 'Stage updated' });
      } catch {
        toast({ title: 'Error', variant: 'destructive' });
      }
    }
  };

  const handleWon = async () => {
    if (id) {
      try {
        await markDealWon(id);
        await fetchDealById(id);
        toast({ title: 'Deal marked as won!' });
      } catch {
        toast({ title: 'Error', variant: 'destructive' });
      }
    }
  };

  const handleLost = async () => {
    if (id) {
      try {
        await markDealLost(id, 'Lost to competitor');
        await fetchDealById(id);
        toast({ title: 'Deal marked as lost' });
      } catch {
        toast({ title: 'Error', variant: 'destructive' });
      }
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  if (!selectedDeal) {
    return <div className="p-6">Deal not found</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/deals')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{selectedDeal.title}</h1>
              <Badge style={{ backgroundColor: selectedDeal.stage_color + '20', color: selectedDeal.stage_color }} variant="outline">
                {selectedDeal.stage}
              </Badge>
            </div>
            <p className="text-muted-foreground">{selectedDeal.deal_number}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowEditDialog(true)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit Deal
          </Button>
          {selectedDeal.status === 'open' && (
            <>
              <Button variant="outline" className="text-green-600 border-green-600 hover:bg-green-50" onClick={handleWon}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark Won
              </Button>
              <Button variant="outline" className="text-red-600 border-red-600 hover:bg-red-50" onClick={handleLost}>
                <XCircle className="h-4 w-4 mr-2" />
                Mark Lost
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stage Pipeline */}
      {selectedDeal.status === 'open' && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {stages.filter(s => !s.is_won && !s.is_lost).map((stage) => (
            <Button
              key={stage.id}
              variant={selectedDeal.stage === stage.name ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleStageChange(stage.name)}
              style={selectedDeal.stage === stage.name ? { backgroundColor: stage.color } : {}}
            >
              {stage.name}
            </Button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="activities">
            <TabsList>
              <TabsTrigger value="activities">Activities</TabsTrigger>
              <TabsTrigger value="reminders">Reminders</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>
            <TabsContent value="activities" className="mt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Activity Timeline</h3>
                <Button size="sm" onClick={() => setShowActivityDialog(true)}>
                  Add Activity
                </Button>
              </div>
              <ActivityTimeline activities={activities} />
            </TabsContent>
            <TabsContent value="documents" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Deal Documents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <AttachmentSection entityType="deal" entityId={id!} />
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="reminders" className="mt-4">
              <ReminderList dealId={id!} reminders={localReminders} />
            </TabsContent>
            <TabsContent value="notes" className="mt-4">
              <NoteList dealId={id!} notes={localNotes} />
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Deal Value</CardTitle></CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{formatCurrency(selectedDeal.value)}</div>
              <p className="text-muted-foreground">{selectedDeal.probability}% probability</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {selectedDeal.company_name && (
                <div className="flex items-center gap-3">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedDeal.company_name}</span>
                </div>
              )}
              {selectedDeal.contact_name && (
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedDeal.contact_name}</span>
                </div>
              )}
              {selectedDeal.expected_close_date && (
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>Expected: {formatDate(selectedDeal.expected_close_date)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-4 border-t">
                <span className="text-muted-foreground">Assigned To</span>
                <span>{selectedDeal.assigned_to_name || '-'}</span>
              </div>
              {selectedDeal.customer_type && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Customer Type</span>
                  <Badge variant="outline">{customerTypeLabels[selectedDeal.customer_type] || selectedDeal.customer_type}</Badge>
                </div>
              )}
              {selectedDeal.product_skus && selectedDeal.product_skus.length > 0 && (
                <div className="pt-2">
                  <span className="text-muted-foreground flex items-center gap-2 mb-2">
                    <Package className="h-4 w-4" />
                    Product SKUs
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {selectedDeal.product_skus.map((sku, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{sku}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {selectedDeal.lead_id && (
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Link2 className="h-4 w-4" /> Source Lead</CardTitle></CardHeader>
              <CardContent>
                <Link 
                  to={`/leads/${selectedDeal.lead_id}`}
                  className="text-primary hover:underline flex items-center gap-2"
                >
                  {selectedDeal.lead_number || 'View Lead'}
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Activity Dialog */}
      <ActivityFormDialog
        open={showActivityDialog}
        onOpenChange={setShowActivityDialog}
        entityType="deal"
        entityId={id}
      />

      {/* Edit Deal Dialog */}
      <DealFormDialog
        open={showEditDialog}
        onOpenChange={(open) => {
          setShowEditDialog(open);
          if (!open) fetchDealById(id!);
        }}
        deal={selectedDeal}
      />
    </div>
  );
}
